import {
    DELIVERY_FEE,
    PICKUP_FEE,
    USAGE_MULTIPLE,
    REQUIRED_UPFRONT,
    REQUIRED_UPFRONT_OPTIONAL,
    ONLY_CHARGE_ONCE_FOR_ENTIRE_CART,
    CHARGE_FOR_THIS_ITEM_INDIVIDUALLY,
    FLAT_RATE,
    PERCENTAGE_OF_RENTAL,
} from '../../util/constants';
import { convertPrice, unitDivisor } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { addTime, getStartOf } from '../../util/dates';
import { determinePackageType } from '../../util/listingAvailabilityHelpers';
import isEqual from 'lodash/isEqual';
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';

const { Money } = sdkTypes;

/**
 * Find delivery/pickup fee with highest rate among all items
 * Compares rates across all items and returns the one with highest rate
 */
export const getHighestRateDeliveryPickupFees = (items, exchangeRates, currentUserCurrency) => {
    let highestDeliveryFee = null;
    let highestPickupFee = null;
    let highestDeliveryRate = 0;
    let highestPickupRate = 0;

    items.forEach(item => {
        const listing = item.listing;
        const additionalFees = listing?.attributes?.publicData?.additionalFees || [];
        const basePrice = listing?.attributes?.price;
        const baseCurrency = basePrice?.currency;

        additionalFees.forEach(fee => {
            if (fee.feeType === DELIVERY_FEE) {
                // Convert rate to currentUserCurrency for comparison
                const { rate } = fee;
                if (rate && baseCurrency) {
                    const divisor = unitDivisor(baseCurrency);
                    const updatedRate = rate * divisor;

                    let rateMoney = null;
                    if (exchangeRates?.length) {
                        rateMoney = convertPrice(
                            exchangeRates,
                            updatedRate,
                            baseCurrency,
                            currentUserCurrency
                        );
                    }

                    if (!rateMoney) {
                        rateMoney = new Money(updatedRate, baseCurrency);
                    }

                    // Compare amounts (best effort; conversion above should ensure same currency)
                    const rateAmount = rateMoney.amount || 0;
                    if (rateAmount > highestDeliveryRate) {
                        highestDeliveryRate = rateAmount;
                        highestDeliveryFee = {
                            ...fee,
                            rate: rateMoney,
                        };
                    }
                }
            }

            if (fee.feeType === PICKUP_FEE) {
                const { rate } = fee;
                if (rate && baseCurrency) {
                    const divisor = unitDivisor(baseCurrency);
                    const updatedRate = rate * divisor;

                    let rateMoney = null;
                    if (exchangeRates?.length) {
                        rateMoney = convertPrice(
                            exchangeRates,
                            updatedRate,
                            baseCurrency,
                            currentUserCurrency
                        );
                    }

                    if (!rateMoney) {
                        rateMoney = new Money(updatedRate, baseCurrency);
                    }

                    const rateAmount = rateMoney.amount || 0;
                    if (rateAmount > highestPickupRate) {
                        highestPickupRate = rateAmount;
                        highestPickupFee = {
                            ...fee,
                            rate: rateMoney,
                        };
                    }
                }
            }
        });
    });

    return {
        deliveryFee: highestDeliveryFee,
        pickupFee: highestPickupFee,
    };
};

// Reference currency for winner-selection math (NOT display) - the underlying
// listing's own currency, matching the server's baseCurrency fallback
// (server/api-util/lineItems.js: the first item's price currency when no explicit
// base listing is designated). Deliberately not currentUserCurrency: that can drift
// with live exchange-rate updates between when the customer sees this comparison and
// when they actually check out, which could make the client and server pick different
// winners for the exact same cart. Display labels are still converted to
// currentUserCurrency separately, in buildFeeDisplay below, after the winner is
// already decided - only the DECISION uses this stable basis.
const getReferenceCurrency = items => items[0]?.listing?.attributes?.price?.currency;

const convertRateToCurrency = (fee, listingCurrency, exchangeRates, toCurrency) => {
    const divisor = unitDivisor(listingCurrency);
    const rateInSubunits = fee.rate * divisor;
    const converted = convertPrice(exchangeRates, rateInSubunits, listingCurrency, toCurrency);
    return converted?.amount ?? rateInSubunits;
};

/**
 * Single source of truth for "if this candidate fee config won, what would it
 * actually cost, in one common currency" - used to pick a winner across MIXED
 * calculation types (flat rate vs percentage-of-rental vs usage-multiple), which the
 * previous per-calculation-type grouping never did (see pickHighestFeeAcrossTypes
 * below for why that was a real bug, not just a display quirk).
 *
 * Usage-multiple candidates are compared by rate alone, never rate x usage - usage
 * isn't known in any way that's safe to compare across items at this point, and the
 * customer's own usage entry must never be able to retroactively change which config
 * wins (that would make the winner non-deterministic from their point of view, and
 * open a gaming vector where entering a small usage number could steer selection
 * toward a cheaper fee). This mirrors server/api-util/lineItems.js's
 * getUpfrontFeeLineItems exactly, so the option shown as selected here is always the
 * one the server will actually charge.
 */
const calculateComparisonValue = (fee, groupTotalItemPrice, listingCurrency, exchangeRates, referenceCurrency) => {
    if (fee.feeCalculationType === PERCENTAGE_OF_RENTAL) {
        // "Percentage of rental" for a once-per-cart fee means percentage of the whole
        // shipment/order it covers, not just the one item whose listing happens to
        // define the percentage - groupTotalItemPrice is already the currency-converted
        // sum of every item sharing this fee decision (see pickHighestFeeAcrossTypes).
        return (fee.percentage * groupTotalItemPrice) / 100;
    }
    // FLAT_RATE and USAGE_MULTIPLE both compare by converted rate alone.
    return convertRateToCurrency(fee, listingCurrency, exchangeRates, referenceCurrency);
};

/**
 * Picks exactly one winning fee config from a set of candidates sharing the same
 * feeType, regardless of each candidate's own calculation type. Returns null if there
 * are no candidates.
 *
 * @param {Array} candidates - [{ fee, itemPrice: Money (listing's own currency),
 *   listingCurrency, itemId, itemTitle }]
 */
const pickHighestFeeAcrossTypes = (candidates, exchangeRates, referenceCurrency) => {
    if (!candidates.length) return null;

    const groupTotalItemPrice = candidates.reduce((sum, c) => {
        const converted = convertPrice(exchangeRates, c.itemPrice.amount, c.itemPrice.currency, referenceCurrency);
        return sum + (converted?.amount ?? 0);
    }, 0);

    let winner = null;
    let winnerComparisonValue = -1;

    candidates.forEach(candidate => {
        const comparisonValue = calculateComparisonValue(
            candidate.fee,
            groupTotalItemPrice,
            candidate.listingCurrency,
            exchangeRates,
            referenceCurrency
        );
        if (comparisonValue > winnerComparisonValue) {
            winnerComparisonValue = comparisonValue;
            winner = candidate;
        }
    });

    return { winner, groupTotalItemPrice };
};

/**
 * Builds the display-ready fee object DeliveryReturnSelectField/CartAddonsField
 * expect - rate converted to currentUserCurrency for the label text (formatMoney needs
 * a Money in a sensible display currency; this is purely presentational and separate
 * from the referenceCurrency math above that decided the winner).
 */
const buildFeeDisplay = (candidate, exchangeRates, currentUserCurrency) => {
    const { fee, listingCurrency, itemTitle, itemId, itemPrice } = candidate;
    const rateMoney =
        convertPrice(exchangeRates, fee.rate * unitDivisor(listingCurrency), listingCurrency, currentUserCurrency) ||
        new Money(fee.rate * unitDivisor(listingCurrency), listingCurrency);

    return {
        ...fee,
        rate: rateMoney,
        originalRate: fee.rate,
        listingCurrency,
        itemTitle,
        itemId,
        itemPrice,
    };
};

/**
 * Extract delivery/pickup configuration from items
 * Separates global (oncePerCart) vs per-item (oncePerItem) fees
 */
export const getCartDeliveryPickupConfig = (items, highestRateFees, exchangeRates, currentUserCurrency) => {
    if (!items.length) {
        return {
            deliveryPickup: null,
            listing: null,
        };
    }

    const firstItem = items[0];
    const listing = firstItem?.listing;
    const referenceCurrency = getReferenceCurrency(items);

    // Track flags
    let hasDeliveryOption = false;
    let hasPickupOption = false;

    // Collected regardless of calculation type - "highest wins" is applied once,
    // across every calculation type together, by pickHighestFeeAcrossTypes below.
    // Grouping by calculation type here (the historical bug) let a flat-rate delivery
    // fee and a percentage-of-rental delivery fee each "win" their own narrow group
    // independently, so both got shown/charged instead of one.
    const globalDeliveryCandidates = [];
    const globalPickupCandidates = [];
    const perItemDeliveryFees = [];
    const perItemPickupFees = [];

    items.forEach((item) => {
        const listing = item.listing;
        const fees = listing?.attributes?.publicData?.additionalFees || [];
        const basePrice = listing?.attributes?.price;
        const baseCurrency = basePrice?.currency;
        const itemTitle = listing?.attributes?.title;
        const itemPrice = basePrice; // Money object

        fees.forEach(fee => {
            if (fee.feeType !== DELIVERY_FEE && fee.feeType !== PICKUP_FEE) return;

            if (fee.feeType === DELIVERY_FEE) hasDeliveryOption = true;
            if (fee.feeType === PICKUP_FEE) hasPickupOption = true;

            const candidate = {
                fee,
                itemPrice,
                listingCurrency: baseCurrency,
                itemTitle,
                itemId: item.id,
            };

            if (fee.checkoutOptions === ONLY_CHARGE_ONCE_FOR_ENTIRE_CART) {
                (fee.feeType === DELIVERY_FEE ? globalDeliveryCandidates : globalPickupCandidates).push(candidate);
            } else if (fee.checkoutOptions === CHARGE_FOR_THIS_ITEM_INDIVIDUALLY) {
                const convertedFee = buildFeeDisplay(candidate, exchangeRates, currentUserCurrency);
                (fee.feeType === DELIVERY_FEE ? perItemDeliveryFees : perItemPickupFees).push(convertedFee);
            }
        });
    });

    const deliveryResult = pickHighestFeeAcrossTypes(globalDeliveryCandidates, exchangeRates, referenceCurrency);
    const pickupResult = pickHighestFeeAcrossTypes(globalPickupCandidates, exchangeRates, referenceCurrency);

    const globalDeliveryFee = deliveryResult
        ? { ...buildFeeDisplay(deliveryResult.winner, exchangeRates, currentUserCurrency), groupTotalItemPrice: deliveryResult.groupTotalItemPrice }
        : null;
    const globalPickupFee = pickupResult
        ? { ...buildFeeDisplay(pickupResult.winner, exchangeRates, currentUserCurrency), groupTotalItemPrice: pickupResult.groupTotalItemPrice }
        : null;

    // Required if ANY item's config requires it - independent of which config wins the
    // price comparison. "Required" is a per-item/per-listing policy an owner sets
    // ("this item must be delivered"), not a property of whichever fee happens to
    // price highest; deriving it from the winning config alone (the previous behavior)
    // let an owner's mandatory-delivery policy be silently dropped whenever a
    // different, optional, cheaper fee won the comparison.
    const isDeliveryRequired = globalDeliveryCandidates.some(c => c.fee.whenToApply === REQUIRED_UPFRONT);
    const isPickupRequired = globalPickupCandidates.some(c => c.fee.whenToApply === REQUIRED_UPFRONT);

    return {
        deliveryPickup: {
            // Global fees (for root-level form fields)
            globalDeliveryFee,
            globalPickupFee,
            // Per-item fees (for item-specific form fields)
            perItemDeliveryFees,
            perItemPickupFees,
            // Flags
            hasDeliveryOption,
            hasPickupOption,
            isDeliveryRequired,
            isPickupRequired,
        },
        listing,
    };
};

/**
 * Single source of truth for whether a fee is rendered once for the whole cart
 * (getCartAddonsConfig, below) or once per item (CartDrawerItem's own per-item fields).
 * Those two call sites must stay exact logical opposites of each other on this check,
 * or a fee either renders twice (both places show it) or nowhere (neither does), with
 * no error surfaced either way. Deriving both from this one predicate instead of two
 * independently-maintained `checkoutOptions === /!==` conditions guarantees they can't
 * drift apart on a future edit to either file.
 */
export const isOncePerCartFee = fee => fee.checkoutOptions === ONLY_CHARGE_ONCE_FOR_ENTIRE_CART;

/**
 * Same "oncePerCart items get grouped by feeType+feeCalculationType and the highest
 * config wins, oncePerItem items stay scoped to their own item" rule as
 * getCartDeliveryPickupConfig above, generalized to every OTHER additionalFees entry
 * (cleaning fee, setup fee, etc.) - server/api-util/lineItems.js's getUpfrontFeeLineItems
 * applies this identically to ALL fee types, not just delivery/pickup, but the client
 * only had it for delivery/pickup until now. Without this, a oncePerCart addon
 * configured on 2+ items rendered as N separate per-item rows (one under each item,
 * each with its own price) instead of one shared row using the highest rate - which
 * also meant a user could select/deselect it differently per item, producing a
 * selectedFeeTypes payload that doesn't line up with what the server actually charges
 * (a single highest-rate line item for the whole group).
 *
 * checkoutOptions is read per-item-per-fee (matching the server), not fleet-wide - the
 * same feeType can legitimately be oncePerCart on one listing and oncePerItem on
 * another within the same cart, and each is handled according to its own listing's
 * config.
 *
 * Returns:
 * - globalAddons: { usageMultipleCharges, optionalCharges, requiredCharges } - one
 *   winning fee config per oncePerCart feeType+feeCalculationType group, in the same
 *   shape organizeFees().addons already uses, ready for a single shared UI section.
 *
 * (CartDrawerItem.js excludes these from its own per-item rendering via the negation
 * of the same `isOncePerCartFee` predicate this function uses below - that decision
 * doesn't need cross-item data, only this fee's own config, but deriving both from one
 * shared predicate keeps the two guaranteed in sync.)
 */
export const getCartAddonsConfig = (items, exchangeRates, currentUserCurrency) => {
    const referenceCurrency = getReferenceCurrency(items);
    // Grouped by feeType ALONE - same reasoning as getCartDeliveryPickupConfig above.
    // A flat-rate cleaning fee on one item and a percentage-of-rental cleaning fee on
    // another must compete for the same slot, not each independently "win" a narrow
    // same-calculation-type group and both end up rendered/charged.
    const groupsByFeeType = {}; // key: feeType -> candidates[]

    items.forEach(item => {
        const listing = item.listing;
        const fees = listing?.attributes?.publicData?.additionalFees || [];
        const basePrice = listing?.attributes?.price;
        const baseCurrency = basePrice?.currency;
        const itemTitle = listing?.attributes?.title;
        const itemPrice = basePrice;

        fees.forEach(fee => {
            if (fee.feeType === DELIVERY_FEE || fee.feeType === PICKUP_FEE) return; // handled by getCartDeliveryPickupConfig
            if (!isOncePerCartFee(fee)) return; // oncePerItem - CartDrawerItem already renders these correctly on its own

            if (!groupsByFeeType[fee.feeType]) groupsByFeeType[fee.feeType] = [];
            groupsByFeeType[fee.feeType].push({
                fee,
                itemPrice,
                listingCurrency: baseCurrency,
                itemTitle,
                itemId: item.id,
            });
        });
    });

    const globalAddons = { usageMultipleCharges: [], optionalCharges: [], requiredCharges: [] };

    Object.values(groupsByFeeType).forEach(candidates => {
        const result = pickHighestFeeAcrossTypes(candidates, exchangeRates, referenceCurrency);
        if (!result) return;

        // Required if ANY item's config for this feeType requires it - independent of
        // which config wins the price comparison, same reasoning as delivery/pickup
        // above: an owner's mandatory-fee policy can't be silently dropped just
        // because a different, optional, cheaper config happened to win.
        const isRequiredByAny = candidates.some(c => c.fee.whenToApply === REQUIRED_UPFRONT);
        const fee = {
            ...buildFeeDisplay(result.winner, exchangeRates, currentUserCurrency),
            groupTotalItemPrice: result.groupTotalItemPrice,
            whenToApply: isRequiredByAny ? REQUIRED_UPFRONT : REQUIRED_UPFRONT_OPTIONAL,
        };

        if (fee.feeCalculationType === USAGE_MULTIPLE) {
            globalAddons.usageMultipleCharges.push(fee);
        } else if (isRequiredByAny) {
            globalAddons.requiredCharges.push(fee);
        } else {
            globalAddons.optionalCharges.push(fee);
        }
    });

    return { globalAddons };
};

/**
 * Get existing delivery/pickup values from items
 */
const getExistingDeliveryPickupValues = (items) => {
    if (!items?.length) return {};

    const pickupAtOwnerLocationSelected = items.some(it => it.pickupAtOwnerLocation);
    const returnAtOwnerLocationSelected = items.some(it => it.returnAtOwnerLocation);

    const maxDeliveryUsageMultiple = items.reduce((max, it) => {
        const v = it?.usageMultipleValues?.[DELIVERY_FEE];
        return typeof v === 'number' ? Math.max(max, v) : max;
    }, 0);
    const maxPickupUsageMultiple = items.reduce((max, it) => {
        const v = it?.usageMultipleValues?.[PICKUP_FEE];
        return typeof v === 'number' ? Math.max(max, v) : max;
    }, 0);

    // Cart items are persisted with `selectedFeeTypes` (see cart.duck.js's addToCart/
    // updateCart write paths) - `otherOptionalFees` is only ever accepted as a legacy
    // input there, never written back onto a stored item, so reading it here always
    // returned [], silently dropping a paid delivery/pickup selection the user already
    // made. Falling back to otherOptionalFees keeps this working for any pre-existing
    // cart items that predate the selectedFeeTypes rename.
    const selectedOptionalFees = Array.from(
        new Set(
            items.flatMap(it => (it.selectedFeeTypes || it.otherOptionalFees || []).filter(fee => fee === DELIVERY_FEE || fee === PICKUP_FEE))
        )
    );

    return {
        pickupAtOwnerLocation: pickupAtOwnerLocationSelected ? [true] : [],
        returnAtOwnerLocation: returnAtOwnerLocationSelected ? [true] : [],
        // [DELIVERY_FEE]: maxDeliveryUsageMultiple || 0, Not including in the intial values to avoid wrong value
        // [PICKUP_FEE]: maxPickupUsageMultiple || 0, Not including in the initial values to avoid wrong value
        otherOptionalFees: selectedOptionalFees,
    };
};

/**
 * Build initial form values from items
 * Uses new form structure: { items: {...}, global: {...}, pickupAtOwnerLocation, returnAtOwnerLocation }
 */
export const buildCartModalInitialValues = (items, deliveryPickupConfig, exchangeRates, currentUserCurrency) => {
    if (!items.length) {
        return {
            items: {},
            global: {
                deliveryFee: null,
                pickupFee: null,
                optionalFees: [],
                addonUsage: {},
            },
            pickupAtOwnerLocation: [],
            returnAtOwnerLocation: [],
        };
    }

    // Build items values (per-item structure)
    const itemsValues = {};
    // Existing global (oncePerCart) general-addon selections, collected across all
    // items below - mirrors getExistingDeliveryPickupValues' otherOptionalFees but for
    // every other feeType, since a oncePerCart addon's selection lives on whichever
    // item(s) had it selected (see buildItemsForAPI, which marks it on every item that
    // has that fee configured, not just one "winning" item).
    const existingGlobalOptionalAddonFeeTypes = new Set();
    const existingGlobalUsageAddonValues = {};
    items.forEach(item => {
        const listing = item.listing;
        const publicData = listing?.attributes?.publicData || {};
        const additionalFees = publicData?.additionalFees || [];

        // Get required checkbox-fees from listing (exclude usage-multiple, delivery/
        // pickup, and oncePerCart fees - those are handled once at the cart level by
        // CartAddonsField/getCartAddonsConfig, not per-item. Without the
        // checkoutOptions exclusion here, a required oncePerCart fee would end up in
        // BOTH this item's own requiredFees AND the shared global section, producing a
        // duplicated (though not double-charged, since the server derives fees from
        // listing config rather than counting selectedFeeTypes) entry.
        const requiredFeesFromListing = additionalFees
            .filter(
                fee =>
                    fee.whenToApply === REQUIRED_UPFRONT &&
                    fee.feeCalculationType !== USAGE_MULTIPLE &&
                    fee.feeType !== DELIVERY_FEE &&
                    fee.feeType !== PICKUP_FEE &&
                    fee.checkoutOptions !== ONLY_CHARGE_ONCE_FOR_ENTIRE_CART
            )
            .map(fee => fee.feeType);

        // Per-item (oncePerItem) optional checkbox-fees this item's own selectedFeeTypes
        // already selected. Filtered to feeTypes still actually configured as oncePerItem
        // optional on this listing, so a since-removed fee or one since reconfigured to
        // oncePerCart (now handled by the shared global add-ons section instead) can't
        // leak through as a stale per-item checkbox selection.
        const perItemOptionalFeeTypesOnListing = new Set(
            additionalFees
                .filter(
                    fee =>
                        fee.whenToApply === REQUIRED_UPFRONT_OPTIONAL &&
                        fee.feeCalculationType !== USAGE_MULTIPLE &&
                        fee.feeType !== DELIVERY_FEE &&
                        fee.feeType !== PICKUP_FEE &&
                        fee.checkoutOptions !== ONLY_CHARGE_ONCE_FOR_ENTIRE_CART
                )
                .map(fee => fee.feeType)
        );
        const existingItemFeeTypes = item.selectedFeeTypes || item.otherOptionalFees || [];
        const prefilledOptionalFees = existingItemFeeTypes.filter(feeType =>
            perItemOptionalFeeTypesOnListing.has(feeType)
        );

        const baseValues = {
            quantity: item.quantity ?? 1,

            // Item-specific optional fees (excluding delivery/pickup) - prefilled above
            optionalFees: prefilledOptionalFees,
            // Item-specific required fees (excluding delivery/pickup)
            requiredFees: requiredFeesFromListing,
            // Per-item delivery/pickup usage values (if per-item)
            deliveryFee: null,
            pickupFee: null,
        };

        // Ensure required usage-multiple fees are initialized (auto-filled), and
        // prefill optional usage-multiple fees from what this item already had
        // selected (same reasoning as prefilledOptionalFees above).
        const existingItemFeeTypesSet = new Set(existingItemFeeTypes);
        additionalFees.forEach(fee => {
            const isUsageMultiple = fee.feeCalculationType === USAGE_MULTIPLE;
            const isRequired = fee.whenToApply === REQUIRED_UPFRONT;
            const isDeliveryPickup = fee.feeType === DELIVERY_FEE || fee.feeType === PICKUP_FEE;
            const isGlobal = fee.checkoutOptions === ONLY_CHARGE_ONCE_FOR_ENTIRE_CART;
            if (isDeliveryPickup) return;

            if (isGlobal) {
                // oncePerCart - prefill collected separately below, applied once to
                // `global` (not per-item) after this items.forEach loop finishes.
                if (isUsageMultiple) {
                    if (item.usageMultipleValues?.[fee.feeType] != null) {
                        existingGlobalUsageAddonValues[fee.feeType] = Math.max(
                            existingGlobalUsageAddonValues[fee.feeType] || 0,
                            item.usageMultipleValues[fee.feeType]
                        );
                    } else if (isRequired && existingGlobalUsageAddonValues[fee.feeType] == null) {
                        // Same default-to-1 rule as the per-item required case below -
                        // without this, a required oncePerCart usage fee with no prior
                        // selection left global.addonUsage[feeType] unset, so
                        // CartAddonsField's required-field validation on that
                        // input would fail even though buildItemsForAPI silently
                        // defaults the actual charge to usage=1 regardless - a required
                        // field the user could never satisfy despite already being
                        // charged correctly.
                        existingGlobalUsageAddonValues[fee.feeType] = 1;
                    }
                } else if (existingItemFeeTypesSet.has(fee.feeType)) {
                    existingGlobalOptionalAddonFeeTypes.add(fee.feeType);
                }
                return;
            }

            if (isUsageMultiple) {
                // Honor a previously-entered usage value first, for both required and
                // optional fees - only default to 1 when genuinely never set. The
                // required branch used to unconditionally reset to 1 regardless of what
                // the user had actually entered before, discarding it every time the
                // cart drawer reopened; the oncePerCart branch above never had this bug.
                if (item.usageMultipleValues?.[fee.feeType] != null) {
                    baseValues[fee.feeType] = item.usageMultipleValues[fee.feeType];
                } else if (isRequired) {
                    baseValues[fee.feeType] = 1;
                }
            }
        });

        itemsValues[item.id] = baseValues;
    });

    // Build delivery/pickup initial values (apply BookingDatesForm rules)
    const { deliveryPickup } = deliveryPickupConfig || {};
    const {
        globalDeliveryFee,
        globalPickupFee,
        perItemDeliveryFees = [],
        perItemPickupFees = [],
        hasDeliveryOption,
        hasPickupOption,
        isDeliveryRequired,
        isPickupRequired,
    } = deliveryPickup || {};

    // Rule 1: If no delivery option → auto-select self pickup (disabled)
    const shouldForceSelfPickup = !hasDeliveryOption;

    // Rule 2: If no pickup option → auto-select self return (disabled)
    const shouldForceSelfReturn = !hasPickupOption;

    // Build global delivery/pickup values
    const globalValues = {
        deliveryFee: null,
        pickupFee: null,
        optionalFees: [],
    };

    // Global delivery fee (usage multiple)
    if (globalDeliveryFee?.feeCalculationType === USAGE_MULTIPLE) {
        globalValues.deliveryFee = isDeliveryRequired ? 1 : null;
    } else if (globalDeliveryFee) {
        // Global delivery fee (flat/percentage) - add to optionalFees if selected
        if (isDeliveryRequired) {
            globalValues.optionalFees.push(DELIVERY_FEE);
        }
    }

    // Global pickup fee (usage multiple)
    if (globalPickupFee?.feeCalculationType === USAGE_MULTIPLE) {
        globalValues.pickupFee = isPickupRequired ? 1 : null;
    } else if (globalPickupFee) {
        // Global pickup fee (flat/percentage) - add to optionalFees if selected
        if (isPickupRequired) {
            globalValues.optionalFees.push(PICKUP_FEE);
        }
    }

    // Set per-item delivery/pickup values
    // Looked up per per-item delivery/pickup fee below to read back a previously
    // entered usage value - `fee` there is the display-built candidate object, not the
    // raw cart item, so it doesn't carry usageMultipleValues itself.
    const itemById = new Map(items.map(item => [item.id, item]));

    perItemDeliveryFees.forEach(fee => {
        const itemId = fee.itemId;
        if (!itemsValues[itemId]) return;

        if (fee.feeCalculationType === USAGE_MULTIPLE) {
            // Honor a previously-entered usage value first, same fix as the general
            // per-item usage-multiple branch above - only default to 1 when required
            // and genuinely never set, never reset an optional field to null when a
            // real value already exists.
            const existingUsage = itemById.get(itemId)?.usageMultipleValues?.[DELIVERY_FEE];
            itemsValues[itemId].deliveryFee =
                existingUsage != null ? existingUsage : fee.whenToApply === REQUIRED_UPFRONT ? 1 : null;
        } else {
            // Per-item flat/percentage - handled via optionalFees/requiredFees
            if (fee.whenToApply === REQUIRED_UPFRONT) {
                if (!itemsValues[itemId].requiredFees.includes(DELIVERY_FEE)) {
                    itemsValues[itemId].requiredFees.push(DELIVERY_FEE);
                }
            } else {
                // Optional - prefill from whatever was already selected on this item,
                // same as every other per-item optional fee (this one was previously
                // missing entirely, since delivery/pickup were excluded from the
                // general per-item optional-fee prefill above by design).
                const existingFeeTypes = itemById.get(itemId)?.selectedFeeTypes || [];
                if (existingFeeTypes.includes(DELIVERY_FEE) && !itemsValues[itemId].optionalFees.includes(DELIVERY_FEE)) {
                    itemsValues[itemId].optionalFees.push(DELIVERY_FEE);
                }
            }
        }
    });

    perItemPickupFees.forEach(fee => {
        const itemId = fee.itemId;
        if (!itemsValues[itemId]) return;

        if (fee.feeCalculationType === USAGE_MULTIPLE) {
            const existingUsage = itemById.get(itemId)?.usageMultipleValues?.[PICKUP_FEE];
            itemsValues[itemId].pickupFee =
                existingUsage != null ? existingUsage : fee.whenToApply === REQUIRED_UPFRONT ? 1 : null;
        } else {
            // Per-item flat/percentage - handled via optionalFees/requiredFees
            if (fee.whenToApply === REQUIRED_UPFRONT) {
                if (!itemsValues[itemId].requiredFees.includes(PICKUP_FEE)) {
                    itemsValues[itemId].requiredFees.push(PICKUP_FEE);
                }
            } else {
                const existingFeeTypes = itemById.get(itemId)?.selectedFeeTypes || [];
                if (existingFeeTypes.includes(PICKUP_FEE) && !itemsValues[itemId].optionalFees.includes(PICKUP_FEE)) {
                    itemsValues[itemId].optionalFees.push(PICKUP_FEE);
                }
            }
        }
    });

    // getExistingDeliveryPickupValues reads back whatever fulfillment choice was already
    // made when each item was added to cart (e.g. on the listing page's own booking
    // form) - without this, initial values always default to "nothing selected"
    // whenever a delivery/pickup alternative exists (shouldForceSelfPickup/Return only
    // covers the case where self-pickup/return is the ONLY option), discarding a
    // choice the user already made and forcing them to re-select it here before
    // Checkout's validation will pass.
    const existingValues = getExistingDeliveryPickupValues(items);

    return {
        items: itemsValues,
        global: {
            ...globalValues,
            optionalFees: Array.from(
                new Set([
                    ...globalValues.optionalFees,
                    ...(existingValues.otherOptionalFees || []),
                    ...existingGlobalOptionalAddonFeeTypes,
                ])
            ),
            // Global (oncePerCart) general-addon usage-multiple values, keyed by
            // feeType - separate namespace from deliveryFee/pickupFee above so a
            // general addon's feeType can never collide with those two reserved names.
            addonUsage: existingGlobalUsageAddonValues,
        },
        pickupAtOwnerLocation:
            shouldForceSelfPickup || existingValues.pickupAtOwnerLocation?.[0] === true ? [true] : [],
        returnAtOwnerLocation:
            shouldForceSelfReturn || existingValues.returnAtOwnerLocation?.[0] === true ? [true] : [],
    };
};

/**
 * 
 */
export const buildItemsForAPI = (formValues, items) => {
    const {
        items: itemsFormValues = {},
        pickupAtOwnerLocation,
        returnAtOwnerLocation,
        global: {
            deliveryFee: globalDeliveryFee,
            pickupFee: globalPickupFee,
            optionalFees: globalOptionalFees = [],
        } = {},
    } = formValues

    return items.map(item => {
        const itemFormValues = itemsFormValues[item.id] || {};
        const listing = item.listing;
        const publicData = listing?.attributes?.publicData || {};
        const additionalFees = publicData?.additionalFees || [];

        // Collect all selected fee types for this item
        const selectedFeeTypes = [];
        const usageMultipleValues = {};

        // 1. Non-delivery/pickup usage multiple (always per-item)
        Object.keys(itemFormValues).forEach(key => {
            if (key !== 'quantity' &&
                key !== 'deliveryFee' &&
                key !== 'pickupFee' &&
                key !== 'optionalFees' &&
                key !== 'requiredFees' &&
                !isNaN(Number(itemFormValues[key]))) {
                usageMultipleValues[key] = Number(itemFormValues[key]);
                selectedFeeTypes.push(key);
            }
        });

        // 2. Per-item optional/required fees (flat/percentage)
        itemFormValues.optionalFees?.forEach(feeType => {
            if (feeType !== DELIVERY_FEE && feeType !== PICKUP_FEE) {
                selectedFeeTypes.push(feeType);
            }
        });
        itemFormValues.requiredFees?.forEach(feeType => {
            if (feeType !== DELIVERY_FEE && feeType !== PICKUP_FEE) {
                selectedFeeTypes.push(feeType);
            }
        });

        // 3. Delivery/pickup fees - check listing config to determine if global or per-item
        const deliveryFeeConfig = additionalFees.find(f => f.feeType === DELIVERY_FEE);
        const pickupFeeConfig = additionalFees.find(f => f.feeType === PICKUP_FEE);

        if (deliveryFeeConfig) {
            const isGlobal = deliveryFeeConfig.checkoutOptions === ONLY_CHARGE_ONCE_FOR_ENTIRE_CART;

            if (isGlobal) {
                // Global delivery: check global form values
                const isSelected = deliveryFeeConfig.feeCalculationType === USAGE_MULTIPLE
                    ? (!isNaN(Number(globalDeliveryFee)))
                    : globalOptionalFees.includes(DELIVERY_FEE);

                if (isSelected) {
                    if (deliveryFeeConfig.feeCalculationType === USAGE_MULTIPLE) {
                        usageMultipleValues[DELIVERY_FEE] = Number(globalDeliveryFee);
                    }
                    selectedFeeTypes.push(DELIVERY_FEE);
                }
            } else {
                // Per-item delivery: check item form values
                const isSelected = deliveryFeeConfig.feeCalculationType === USAGE_MULTIPLE
                    ? (!isNaN(Number(itemFormValues.deliveryFee)))
                    : (itemFormValues.optionalFees?.includes(DELIVERY_FEE) || itemFormValues.requiredFees?.includes(DELIVERY_FEE));

                if (isSelected) {
                    if (deliveryFeeConfig.feeCalculationType === USAGE_MULTIPLE) {
                        usageMultipleValues[DELIVERY_FEE] = Number(itemFormValues.deliveryFee);
                    }
                    selectedFeeTypes.push(DELIVERY_FEE);
                }
            }
        }

        // Same logic for pickupFee...
        if (pickupFeeConfig) {
            const isGlobal = pickupFeeConfig.checkoutOptions === ONLY_CHARGE_ONCE_FOR_ENTIRE_CART;

            if (isGlobal) {
                const isSelected = pickupFeeConfig.feeCalculationType === USAGE_MULTIPLE
                    ? (!isNaN(Number(globalPickupFee)))
                    : globalOptionalFees.includes(PICKUP_FEE);

                if (isSelected) {
                    if (pickupFeeConfig.feeCalculationType === USAGE_MULTIPLE) {
                        usageMultipleValues[PICKUP_FEE] = Number(globalPickupFee);
                    }
                    selectedFeeTypes.push(PICKUP_FEE);
                }
            } else {
                const isSelected = pickupFeeConfig.feeCalculationType === USAGE_MULTIPLE
                    ? (!isNaN(Number(itemFormValues.pickupFee)))
                    : (itemFormValues.optionalFees?.includes(PICKUP_FEE) || itemFormValues.requiredFees?.includes(PICKUP_FEE));

                if (isSelected) {
                    if (pickupFeeConfig.feeCalculationType === USAGE_MULTIPLE) {
                        usageMultipleValues[PICKUP_FEE] = Number(itemFormValues.pickupFee);
                    }
                    selectedFeeTypes.push(PICKUP_FEE);
                }
            }
        }

        // 4. General (non-delivery/pickup) oncePerCart addon fees - same shared-selection
        // pattern as delivery/pickup above: every item that has this feeType configured
        // as oncePerCart gets marked selected/given the same usage value when the shared
        // global control says so, regardless of which item's rate "won" the display
        // comparison in getCartAddonsConfig. The server (server/api-util/lineItems.js)
        // does its own highest-wins aggregation across whichever items come through
        // marked selected here - this only needs to get the selection right, not decide
        // which item's price applies.
        additionalFees
            .filter(
                fee =>
                    fee.feeType !== DELIVERY_FEE &&
                    fee.feeType !== PICKUP_FEE &&
                    fee.checkoutOptions === ONLY_CHARGE_ONCE_FOR_ENTIRE_CART
            )
            .forEach(fee => {
                const isRequired = fee.whenToApply === REQUIRED_UPFRONT;
                if (fee.feeCalculationType === USAGE_MULTIPLE) {
                    // Required global usage fees have no checkbox/toggle - always
                    // selected, defaulting to usage=1 same as the per-item required
                    // case above, unless the shared global input has its own value.
                    const usageValue = formValues.global?.addonUsage?.[fee.feeType];
                    if (usageValue != null && !isNaN(Number(usageValue))) {
                        usageMultipleValues[fee.feeType] = Number(usageValue);
                        selectedFeeTypes.push(fee.feeType);
                    } else if (isRequired) {
                        usageMultipleValues[fee.feeType] = 1;
                        selectedFeeTypes.push(fee.feeType);
                    }
                } else if (isRequired || globalOptionalFees.includes(fee.feeType)) {
                    selectedFeeTypes.push(fee.feeType);
                }
            });

        return {
            id: item.id, // ADD: Include item id for Redux updates
            // updateCart needs authorId to locate the item's store/validate quantity -
            // relying on callers to assume a single shared author (true today since the
            // drawer is scoped to one listing's author, but not guaranteed by this
            // return shape itself) was a latent landmine. Carrying it through explicitly
            // removes that implicit assumption at the call sites.
            authorId: item.authorId,
            bookingDates: item.bookingDates,
            timeZone: item.timeZone,
            listingId: item.listingId,
            listing: item.listing,
            package: item.package,
            quantity: itemFormValues.quantity ?? item.quantity ?? 1,
            usageMultipleValues,
            selectedFeeTypes, // NEW: simple array of selected fee types
            pickupAtOwnerLocation: pickupAtOwnerLocation?.[0] === true,
            returnAtOwnerLocation: returnAtOwnerLocation?.[0] === true,
        };
    });
};

/**
 * RAS-2582: whether a Store Cart item's editable fields (quantity, usage-multiple
 * values, selected fees, pickup/return-at-owner-location) actually differ from the
 * cart item it was built from. buildItemsForAPI always returns fresh objects/arrays
 * even when nothing changed, so this compares values rather than references.
 */
export const hasCartItemChanged = (original, updated) => {
    if (!original || !updated) return false;

    const sortedFeeTypes = list => (list || []).slice().sort();

    return (
        Number(original.quantity ?? 1) !== Number(updated.quantity ?? 1) ||
        !isEqual(original.usageMultipleValues || {}, updated.usageMultipleValues || {}) ||
        !isEqual(sortedFeeTypes(original.selectedFeeTypes), sortedFeeTypes(updated.selectedFeeTypes)) ||
        !!original.pickupAtOwnerLocation !== !!updated.pickupAtOwnerLocation ||
        !!original.returnAtOwnerLocation !== !!updated.returnAtOwnerLocation
    );
};

/**
 * RAS-2582: builds the API-shaped payload for every cart item in the Store Cart form,
 * then filters it down to only the items that actually changed vs. the cart items the
 * form was initialized from - so closing the cart without editing anything never fires
 * an update.
 */
export const getChangedCartItems = (formValues, items) => {
    if (!formValues || !items?.length) return [];

    const itemsWithFormValues = buildItemsForAPI(formValues, items);
    return itemsWithFormValues.filter(updated => {
        const original = items.find(i => i.id === updated.id);
        return hasCartItemChanged(original, updated);
    });
};

/**
 * Human-readable "{start} - {end}" label for a cart booking, in the booking's own
 * time zone. Cart bookingDates.endDate is EXCLUSIVE (the day after the last rental
 * day, the same convention the API and DateRangeInput's pickerEndDateToApiDate use),
 * so a daily booking's end is shifted back one day to the INCLUSIVE last day - a
 * Sat-Sun rental reads "Sep 19 - Sep 20", not "Sep 19 - Sep 21". Hourly bookings carry
 * a real end instant and are shown as-is. Gated on the booking's own duration
 * (determinePackageType) rather than its stored `package` pricing tier, exactly as
 * CartDrawer's own header does, so the two panels can't drift. Shared by
 * CartModalHeader (Store Cart) and CartDrawer (listing page).
 *
 * Returns null when either date is missing.
 */
export const formatCartDateRangeLabel = (bookingDates, timeZone, intl) => {
    if (!bookingDates?.startDate || !bookingDates?.endDate) return null;
    const tz = timeZone || 'Etc/UTC';
    const start = new Date(bookingDates.startDate);
    const end = new Date(bookingDates.endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
    const isHourly = determinePackageType(start, end) === 'hourly';
    const displayEnd = isHourly ? end : addTime(getStartOf(end, 'day', tz), -1, 'days', tz);
    return intl.formatMessage(
        { id: 'CartModal.dateRange' },
        {
            startDate: moment(start).tz(tz).format('MMM D'),
            endDate: moment(displayEnd).tz(tz).format('MMM D, YYYY'),
        }
    );
};
